import React, { useEffect, useRef, useState, useCallback } from "react";

// ============================================================
// ReceiverScreen — the high-contrast public-facing display.
// ------------------------------------------------------------
// * Dark #111 background regardless of app theme
// * Font 32–72 px adjustable
// * Smooth auto-scroll to latest; pause when user scrolls up
// * Floating "Live" pill when paused
// * Fullscreen toggle
// * Screen Wake Lock (silent if unsupported)
// * Edit flash: gold border animation on in-place updates
// * "Waiting for next phrase…" state on clear
// ============================================================

const MIN_SIZE = 32;
const MAX_SIZE = 72;
const DEFAULT_SIZE = 48;
const AT_BOTTOM_THRESHOLD_PX = 50;

export default function ReceiverScreen({ connected, phrases, cleared, onSwitchRole }) {
  const [fontSize, setFontSize] = useState(DEFAULT_SIZE);
  const [atBottom, setAtBottom] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [wakeOn, setWakeOn] = useState(false);
  const scrollRef = useRef(null);
  const wakeRef = useRef(null);
  const flashRef = useRef({}); // phraseId -> {timeout, el}

  // --- Wake Lock ---
  const requestWakeLock = useCallback(async () => {
    if (!("wakeLock" in navigator)) { setWakeOn(false); return; }
    try {
      wakeRef.current = await navigator.wakeLock.request("screen");
      setWakeOn(true);
      wakeRef.current.addEventListener?.("release", () => setWakeOn(false));
    } catch (_) {
      setWakeOn(false);
    }
  }, []);

  useEffect(() => {
    requestWakeLock();
    const onVisibility = () => {
      if (document.visibilityState === "visible") requestWakeLock();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      if (wakeRef.current) { try { wakeRef.current.release(); } catch (_) {} wakeRef.current = null; }
    };
  }, [requestWakeLock]);

  // --- Scroll tracking ---
  const checkAtBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    setAtBottom(dist < AT_BOTTOM_THRESHOLD_PX);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", checkAtBottom, { passive: true });
    checkAtBottom();
    return () => el.removeEventListener("scroll", checkAtBottom);
  }, [checkAtBottom]);

  // Auto-scroll on new phrases (if currently at bottom).
  const lastPhraseIdRef = useRef(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || phrases.length === 0) return;
    const last = phrases[phrases.length - 1];
    const isNew = last.id !== lastPhraseIdRef.current;
    lastPhraseIdRef.current = last.id;
    if (isNew && atBottom) {
      // Smooth scroll to bottom.
      requestAnimationFrame(() => {
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      });
    }
  }, [phrases, atBottom]);

  // --- Edit flash ---
  // Compare previous phrases to new to detect edits and trigger the flash
  const prevPhraseMapRef = useRef(new Map());
  useEffect(() => {
    const prev = prevPhraseMapRef.current;
    phrases.forEach(p => {
      const pr = prev.get(p.id);
      if (pr && (pr.text !== p.text || pr.translation !== p.translation)) {
        const el = document.querySelector(`[data-phrase-id="${p.id}"]`);
        if (el) {
          el.classList.remove("translation-flash");
          // Force reflow so the animation restarts
          // eslint-disable-next-line no-unused-expressions
          el.offsetWidth;
          el.classList.add("translation-flash");
        }
      }
    });
    const next = new Map();
    phrases.forEach(p => next.set(p.id, { text: p.text, translation: p.translation }));
    prevPhraseMapRef.current = next;
  }, [phrases]);

  // --- Fullscreen handling ---
  const toggleFullscreen = async () => {
    const el = document.getElementById("translation-receiver-root");
    if (!document.fullscreenElement) {
      try { await el.requestFullscreen(); setFullscreen(true); } catch (_) {}
    } else {
      try { await document.exitFullscreen(); setFullscreen(false); } catch (_) {}
    }
  };
  useEffect(() => {
    const onFsChange = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  const bump = (delta) => setFontSize(sz => Math.min(MAX_SIZE, Math.max(MIN_SIZE, sz + delta)));
  const goLive = () => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    setAtBottom(true);
  };

  const isCleared = cleared > 0 && phrases.length === 0;

  return (
    <div
      id="translation-receiver-root"
      data-testid="translation-receiver-screen"
      style={{
        position: "relative",
        background: "#111111",
        color: "#FFFFFF",
        borderRadius: fullscreen ? 0 : 12,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: fullscreen ? "100vh" : "min(80vh, 760px)",
      }}
    >
      <style>{`
        @keyframes translationFlash {
          0%   { box-shadow: 0 0 0 0 rgba(212,175,55,0); border-color: transparent; }
          15%  { box-shadow: 0 0 24px 4px rgba(212,175,55,0.55); border-color: #D4AF37; }
          100% { box-shadow: 0 0 0 0 rgba(212,175,55,0); border-color: transparent; }
        }
        .translation-flash {
          animation: translationFlash 1.1s ease-out 1;
        }
      `}</style>

      {/* TOP BAR */}
      <div
        style={{
          display: fullscreen ? "none" : "flex",
          alignItems: "center", gap: 10,
          padding: "10px 14px",
          background: "rgba(0,0,0,0.55)",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <span style={{ fontFamily: "'Cinzel',serif", fontSize: 14, letterSpacing: 1.2, color: "#D4AF37" }}>KC</span>
        <span
          aria-hidden="true"
          style={{
            display: "inline-block", width: 10, height: 10, borderRadius: "50%",
            background: connected ? "#4ade80" : "#f87171",
            boxShadow: `0 0 8px ${connected ? "#4ade80" : "#f87171"}`,
          }}
        />
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5 }} data-testid="translation-receiver-status">
          {connected ? "LIVE" : "RECONNECTING…"}
        </span>
        {wakeOn && (
          <span style={{ fontSize: 11, opacity: 0.75 }} title="Screen stay-awake is on" data-testid="translation-receiver-wake-on">
            🔆 Screen on
          </span>
        )}
        <div style={{ flex: 1 }} />
        <button
          type="button"
          onClick={() => bump(-4)}
          data-testid="translation-receiver-size-down"
          aria-label="Decrease text size"
          style={fsBtn}
        >A−</button>
        <span data-testid="translation-receiver-size-value" style={{ fontSize: 11, opacity: 0.6, width: 32, textAlign: "center" }}>{fontSize}</span>
        <button
          type="button"
          onClick={() => bump(+4)}
          data-testid="translation-receiver-size-up"
          aria-label="Increase text size"
          style={fsBtn}
        >A+</button>
        <button
          type="button"
          onClick={toggleFullscreen}
          data-testid="translation-receiver-fullscreen"
          aria-label="Toggle fullscreen"
          style={fsBtn}
        >⛶</button>
        <button
          type="button"
          onClick={onSwitchRole}
          data-testid="translation-receiver-switch-role"
          style={{ ...fsBtn, padding: "4px 10px" }}
          title="Switch between Sender / Receiver / Observer"
        >Switch</button>
      </div>

      {/* MAIN SCROLL AREA */}
      <div
        ref={scrollRef}
        data-testid="translation-receiver-scroll"
        style={{
          flex: 1,
          overflowY: "auto",
          scrollBehavior: "smooth",
          padding: fullscreen ? "max(20vh, 80px) 24px 24px" : "24px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
          alignItems: "stretch",
        }}
      >
        {isCleared ? (
          <div
            data-testid="translation-receiver-waiting"
            style={{
              margin: "auto", fontSize: Math.max(18, fontSize * 0.4),
              color: "rgba(255,255,255,0.3)", fontStyle: "italic", textAlign: "center",
            }}
          >Waiting for next phrase…</div>
        ) : phrases.length === 0 ? (
          <div style={{
            margin: "auto", fontSize: Math.max(18, fontSize * 0.4),
            color: "rgba(255,255,255,0.25)", fontStyle: "italic", textAlign: "center",
          }}>Awaiting first phrase…</div>
        ) : (
          phrases.map(p => (
            <div
              key={p.id}
              data-phrase-id={p.id}
              data-testid={`translation-receiver-phrase-${p.id}`}
              style={{
                padding: "20px 22px",
                borderRadius: 14,
                background: "#1a1a1a",
                border: "1px solid rgba(255,255,255,0.08)",
                boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
              }}
            >
              {p.text && (
                <div
                  data-testid={`translation-receiver-phrase-source-${p.id}`}
                  style={{
                    fontSize: 18, fontFamily: "'Inter',sans-serif", fontWeight: 400,
                    color: "rgba(255,255,255,0.5)", marginBottom: 10, letterSpacing: 0.2,
                  }}
                >{p.text}</div>
              )}
              <div
                data-testid={`translation-receiver-phrase-translation-${p.id}`}
                style={{
                  fontSize, lineHeight: 1.15,
                  color: "#FFFFFF", fontFamily: "'Inter',sans-serif", fontWeight: 700,
                  letterSpacing: 1, wordBreak: "break-word",
                }}
              >
                {p.translation || <span style={{ opacity: 0.4 }}>…</span>}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10 }}>
                <span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)" }}>
                  {new Date(p.timestamp || Date.now()).toLocaleTimeString()}
                </span>
                {p.edited && (
                  <span
                    data-testid={`translation-receiver-phrase-updated-${p.id}`}
                    style={{ fontSize: 10, color: "#D4AF37", fontWeight: 700, letterSpacing: 1 }}
                  >UPDATED</span>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* FLOATING LIVE PILL — shows when scrolled up */}
      {!atBottom && phrases.length > 0 && (
        <button
          type="button"
          onClick={goLive}
          data-testid="translation-receiver-live-pill"
          style={{
            position: "absolute", left: "50%", top: fullscreen ? 20 : 60,
            transform: "translateX(-50%)",
            background: "#D4AF37", color: "#111",
            padding: "8px 20px", borderRadius: 999,
            fontWeight: 800, fontSize: 13, letterSpacing: 0.6, textTransform: "uppercase",
            border: "none", cursor: "pointer",
            boxShadow: "0 0 24px 4px rgba(212,175,55,0.5)",
            animation: "translationFlash 1.4s ease-in-out infinite",
          }}
        >↓ Live</button>
      )}

      {/* FULLSCREEN FLOATING CONTROL PILL */}
      {fullscreen && (
        <div
          style={{
            position: "absolute", top: 12, right: 12,
            display: "flex", gap: 6,
            background: "rgba(0,0,0,0.6)",
            padding: 6, borderRadius: 999,
          }}
        >
          <button type="button" onClick={() => bump(-4)} data-testid="translation-receiver-fs-size-down" style={fsBtn}>A−</button>
          <button type="button" onClick={() => bump(+4)} data-testid="translation-receiver-fs-size-up" style={fsBtn}>A+</button>
          <button type="button" onClick={toggleFullscreen} data-testid="translation-receiver-fs-exit" style={fsBtn}>⤢</button>
        </div>
      )}
    </div>
  );
}

const fsBtn = {
  padding: "4px 8px",
  background: "rgba(255,255,255,0.08)",
  color: "#FFFFFF",
  border: "1px solid rgba(255,255,255,0.15)",
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 700,
  cursor: "pointer",
  minWidth: 32,
};
